import React from 'react';
import { Helmet } from 'react-helmet';
import GooglePlayBadge from '../GooglePlayBadge/GooglePlayBadge';
import './AppPage.css';

const AartiAppPage = () => {
  return (
    <>
      <Helmet>
        <title>Aarti Sangrah आरती संग्रह - Hindu Religious Aarti Collection | Nirmal Labs</title>
        <meta name="description" content="Download Aarti Sangrah app with complete collection of Hindu religious aartis in Hindi & English. High-quality audio, wallpapers, and devotional features. 10K+ downloads." />
        <meta name="keywords" content="Aarti Sangrah, आरती संग्रह, Hindu aarti, religious app, daily prayers, bhajans, spiritual app, Indian religious app, Ganesh aarti, Shiv aarti, Hanuman aarti" />
        <meta property="og:title" content="Aarti App - Hindu Religious Aarti Collection" />
        <meta property="og:description" content="Complete collection of Hindu aartis and prayers. Download free from Google Play Store." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nirmallabs.com/aarti" />
        <meta property="og:image" content="https://nirmallabs.com/images/aarti-app-screenshot.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aarti App - Hindu Religious Aarti Collection" />
        <meta name="twitter:description" content="Complete collection of Hindu aartis and prayers. Download free from Google Play Store." />
        <meta name="twitter:image" content="https://nirmallabs.com/images/aarti-app-screenshot.jpg" />
        <link rel="canonical" href="https://nirmallabs.com/aarti" />
        
        {/* Structured Data for App */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Aarti App",
            "description": "Complete collection of Hindu religious aartis and daily prayers",
            "applicationCategory": "LifestyleApplication",
            "operatingSystem": "Android",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.5",
              "ratingCount": "1250"
            }
          })}
        </script>
      </Helmet>

      <div className="app-page">
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <img 
                src="https://ik.imagekit.io/l9paoajth/app_logos/logo_aarti.webp?updatedAt=1759892173817&tr=w-120,h-120" 
                alt="Aarti App Icon" 
                className="app-icon"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/120x120/31B8D6/FFFFFF?text=A';
                }}
              />
              <h1>Aarti Sangrah आरती संग्रह - Hindu Religious Aarti Collection</h1>
              <p className="hero-description">
                Complete collection of popular Hindu God & Goddess Aartis in Hindi & English lyrics with audio, 
                wallpapers, and devotional features. Perfect for daily worship and religious practices.
              </p>
              <div className="download-buttons">
                <GooglePlayBadge 
                  href="https://play.google.com/store/apps/details?id=com.nirmallabs.aarti.hindiaarti"
                  size="large"
                  ariaLabel="Download Aarti Sangrah App from Google Play Store"
                />
              </div>
              <div className="app-stats">
                <span className="downloads">10K+ Downloads</span>
                <span className="rating">⭐ 4.0+ Rating</span>
                <span className="category">Books & Reference</span>
              </div>
            </div>
          </div>
        </section>

        <section className="features-section">
          <div className="container">
            <h2>Key Features</h2>
            <div className="features-grid">
              <div className="feature-card">
                <h3>Complete Aarti Collection</h3>
                <p>All God Aarti Collection (Ganesh, Shiv, Hanuman, Ram, Laxmi, Krishna, Durga, Saraswati, Satyanarayan, Shani Dev & more)</p>
              </div>
              <div className="feature-card">
                <h3>High-Quality Audio</h3>
                <p>High-quality Aarti Audio with play/pause/next options and audio controls</p>
              </div>
              <div className="feature-card">
                <h3>Hindi & English Lyrics</h3>
                <p>Lyrics in Hindi & English for every Aarti with beautiful typography</p>
              </div>
              <div className="feature-card">
                <h3>Aarti Tools & Wallpapers</h3>
                <p>Aarti Tools: Bell, Conch, Aarti Lamp, Flowers, Touch Effects & 24+ HD Wallpapers</p>
              </div>
            </div>
          </div>
        </section>

        <section className="screenshots-section">
          <div className="container">
            <h2>App Screenshots</h2>
            <div className="screenshots-carousel">
              <img src="/images/aarti-screenshot-1.jpg" alt="Aarti App Main Screen" />
              <img src="/images/aarti-screenshot-2.jpg" alt="Aarti App Prayer List" />
              <img src="/images/aarti-screenshot-3.jpg" alt="Aarti App Audio Player" />
            </div>
          </div>
        </section>

        <section className="description-section">
          <div className="container">
            <h2>About Aarti App</h2>
            <p>
              The Aarti app by Nirmal Labs is a comprehensive collection of Hindu religious aartis 
              designed for daily worship and spiritual practice. Our app includes traditional aartis 
              with both audio and text formats, making it perfect for devotees of all ages.
            </p>
            <p>
              Features include offline access, multiple languages, and a user-friendly interface 
              that makes it easy to find and play your favorite aartis. Perfect for daily prayers, 
              religious ceremonies, and spiritual learning.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>Is the Aarti app free to download?</h3>
                <p>Yes, the Aarti app is completely free to download and use. All aartis and features are available without any cost.</p>
              </div>
              <div className="faq-item">
                <h3>Can I use the app offline?</h3>
                <p>Yes, once downloaded, you can access all aartis offline. No internet connection is required for daily use.</p>
              </div>
              <div className="faq-item">
                <h3>Are the aartis available in multiple languages?</h3>
                <p>Yes, the app provides aartis in both Hindi and English, with audio and text formats for better understanding.</p>
              </div>
              <div className="faq-item">
                <h3>How many aartis are included in the app?</h3>
                <p>The app includes a comprehensive collection of popular Hindu aartis including Ganesh Aarti, Hanuman Aarti, and many more.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AartiAppPage;
